import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/** Within this of the bottom, the reader is following along rather than reading back. */
const NEAR_BOTTOM_PX = 80;

export interface FollowBottom {
  /** More of a reply was revealed without `growth` changing; stay with it if the reader was. */
  followGrowth: () => void;
}

/**
 * Keep a conversation pinned to its newest line, the way a messenger does —
 * but only for a reader already at the bottom. Yanking someone out of the part
 * they went back to read is worse than letting a reply wait for them.
 *
 * Opening is the exception: a conversation is first shown at its end, which is
 * where it is being carried on. `hasContent` says when there is an end to show.
 *
 * `scroller` is the element that scrolls; null means the page itself does.
 * `growth` is any value that changes whenever the conversation grows — a new
 * message, more of a reply — and every change is a moment to follow it down.
 *
 * Anything else that changes the geometry — a card or image settling, a step
 * opening, the keyboard shrinking the window — is followed as well, before it
 * is painted, so the bottom line never visibly jumps away and back.
 */
export function useFollowBottom(
  scroller: RefObject<HTMLElement | null> | null,
  growth: unknown,
  hasContent: boolean,
): FollowBottom {
  /* Whether this conversation has been shown at its end yet. */
  const opened = useRef(false);
  /*
   * Kept from the reader's own scrolling, not measured when content grows: by
   * the time new text is in the page it has already pushed the bottom away, and
   * measuring then would read every big enough addition as the reader leaving.
   */
  const following = useRef(true);

  const element = useCallback(
    (): HTMLElement | null =>
      scroller ? scroller.current : (document.scrollingElement as HTMLElement | null),
    [scroller],
  );

  /* All the way down — the container's own end, so its bottom padding is scrolled through too. */
  const scrollToEnd = useCallback(() => {
    const target = element();
    if (target) target.scrollTop = target.scrollHeight;
  }, [element]);

  useEffect(() => {
    const target = element();
    if (!target) return;

    /* The page's own scrolling is announced on the window, not on the element. */
    const source: HTMLElement | Window = scroller ? target : window;
    const onScroll = () => {
      following.current =
        target.scrollHeight - target.scrollTop - target.clientHeight < NEAR_BOTTOM_PX;
    };

    source.addEventListener('scroll', onScroll, { passive: true });
    return () => source.removeEventListener('scroll', onScroll);
  }, [element, scroller]);

  /*
   * Resize observations are delivered after layout and before paint, which is
   * exactly when the scroll position can be corrected without a visible step.
   */
  useEffect(() => {
    const target = element();
    if (!target || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      if (opened.current && following.current) scrollToEnd();
    });
    observer.observe(target);
    for (const child of target.children) observer.observe(child);

    return () => observer.disconnect();
  }, [element, scrollToEnd]);

  useLayoutEffect(() => {
    if (opened.current && !following.current) return;

    scrollToEnd();
    opened.current = hasContent;
  }, [growth, hasContent, scrollToEnd]);

  const followGrowth = useCallback(() => {
    if (following.current) scrollToEnd();
  }, [scrollToEnd]);

  return { followGrowth };
}
